import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Nav, { WHATSAPP_ORDER_URL } from "../components/Nav";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import { getPricing } from "../lib/api";

const SITE = "https://riznwash.com";
const EASE = [0.22, 1, 0.36, 1];

function buildJsonLd(services) {
  const productLd = services.map((p) => ({
    "@context": "https://schema.org",
    "@type": "Product",
    "@id": `${SITE}/pricing#${p.id}`,
    name: `${p.name} — RIZ N WASH Gurugram`,
    description: p.description,
    brand: { "@type": "Brand", name: "RIZ N WASH" },
    category: "LaundryService",
    offers: {
      "@type": "Offer",
      priceCurrency: "INR",
      price: p.price,
      availability: "https://schema.org/InStock",
      url: `${SITE}/pricing#${p.id}`,
      priceValidUntil: `${new Date().getFullYear() + 1}-12-31`,
      areaServed: { "@type": "City", name: "Gurugram" },
      eligibleQuantity: {
        "@type": "QuantitativeValue",
        unitText: p.unit,
      },
      seller: {
        "@type": "LocalBusiness",
        name: "RIZ N WASH",
        telephone: "+91-73032-41330",
        areaServed: "Gurugram",
      },
    },
  }));

  return [
    ...productLd,
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "RIZ N WASH", item: SITE },
        { "@type": "ListItem", position: 2, name: "Pricing", item: `${SITE}/pricing` },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: [
        {
          "@type": "Question",
          name: "How much does laundry cost in Gurugram at RIZ N WASH?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Wash & Iron starts at ₹19/piece, ironing/pressing at ₹5/piece and dry cleaning from ₹149/piece. Free doorstep pickup and delivery on orders above ₹299.",
          },
        },
        {
          "@type": "Question",
          name: "Is pickup and delivery included in the price?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Yes — pickup and delivery are free for all orders above ₹299. Below that we add a flat ₹49 runner fee.",
          },
        },
        {
          "@type": "Question",
          name: "How long does it take to get clothes back?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Standard turnaround is 48 hours. Wash & Iron picked up before noon is often returned within 24 hours.",
          },
        },
      ],
    },
  ];
}

export default function Pricing() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getPricing()
      .then((d) => setServices(d.services || []))
      .catch(() => setServices([]))
      .finally(() => setLoading(false));
  }, []);

  const description =
    "Transparent laundry pricing in Gurugram — Wash & Iron from ₹19/piece, Ironing ₹5/piece, Dry Cleaning from ₹149/piece, Premium Care from ₹125/piece. Free doorstep pickup & 48-hour turnaround, available all over Gurugram.";

  return (
    <div className="min-h-screen bg-rw-bg text-rw-ink" data-testid="pricing-page">
      <Seo
        title="Laundry & Dry Cleaning Prices in Gurugram | RIZ N WASH"
        description={description}
        path="/pricing"
        jsonLd={services.length ? buildJsonLd(services) : []}
      />
      <Nav />

      {/* Hero */}
      <section className="relative">
        <div className="max-w-6xl mx-auto px-6 md:px-10 pt-10 pb-10 md:pt-24 md:pb-16">
          <motion.nav
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: EASE }}
            className="overline mb-4 flex flex-wrap items-center gap-2 text-rw-ink3"
            aria-label="Breadcrumb"
          >
            <Link to="/" className="hover:text-rw-ink">Home</Link>
            <span aria-hidden>·</span>
            <span className="text-rw-ink2">Pricing</span>
          </motion.nav>
          <motion.h1
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.1 }}
            className="font-display font-light tracking-tight text-rw-ink leading-[0.98] text-5xl sm:text-6xl lg:text-[6rem]"
            data-testid="pricing-heading"
          >
            Honest laundry pricing<br />
            <em className="italic font-normal text-rw-blue">no hidden fees.</em>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.3 }}
            className="mt-6 md:mt-8 text-base md:text-xl text-rw-ink2 max-w-2xl leading-relaxed"
          >
            The same rate all over Gurugram and all over Delhi NCR — pickup and delivery included on every
            order above ₹299. What you see below is what you pay.
          </motion.p>
        </div>
      </section>

      {/* Pricing table */}
      <section className="bg-rw-bg2 border-y border-rw-ink/10">
        <div className="max-w-5xl mx-auto px-6 md:px-10 py-16 md:py-20">
          {loading ? (
            <div className="py-16 text-center text-rw-ink2">Loading prices…</div>
          ) : services.length === 0 ? (
            <div className="py-16 text-center text-rw-ink2">
              Prices unavailable right now — please refresh or WhatsApp us on +91 73032 41330.
            </div>
          ) : (
            <div className="divide-y divide-rw-ink/10" data-testid="pricing-list">
              {services.map((p, i) => (
                <motion.div
                  key={p.id}
                  id={p.id}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.5, ease: EASE, delay: i * 0.05 }}
                  className="flex items-baseline gap-3 md:gap-5 py-6 md:py-7"
                  data-testid={`pricing-item-${p.id}`}
                >
                  <div className="w-10 md:w-12 font-display text-rw-ink3 text-lg md:text-xl">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-baseline gap-3 flex-wrap">
                      <h2 className="font-display text-2xl md:text-4xl text-rw-ink">
                        {p.name}
                      </h2>
                      <span className="dotted-leader hidden md:block" />
                      <div
                        className="font-display text-2xl md:text-4xl text-rw-ink"
                        data-testid={`pricing-price-${p.id}`}
                      >
                        {p.original_price > p.price && (
                          <span className="line-through text-rw-ink3 text-lg md:text-2xl mr-2 align-middle">₹{p.original_price}</span>
                        )}
                        ₹{p.price}
                        <span className="text-sm md:text-base text-rw-ink3 font-body ml-1">
                          / {p.unit.replace(/^per\s+/i, "")}
                        </span>
                      </div>
                    </div>
                    <p className="text-sm md:text-base text-rw-ink2 mt-2 max-w-2xl leading-relaxed">
                      {p.description}
                    </p>
                    {p.turnaround && (
                      <div className="mt-2 inline-flex items-center gap-2 text-xs uppercase tracking-widest text-rw-ink3">
                        <span className="w-1.5 h-1.5 rounded-full bg-rw-blue" />
                        {p.turnaround} turnaround
                      </div>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          )}

          <div className="mt-10 flex flex-wrap gap-3">
            <a
              href={WHATSAPP_ORDER_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-dark rounded-full px-6 py-3 text-sm font-semibold"
              data-testid="pricing-book-btn"
            >
              Order on WhatsApp →
            </a>
            <Link
              to="/plans"
              className="rounded-full px-6 py-3 border border-rw-ink/20 text-rw-ink hover:bg-rw-ink hover:text-white transition-colors text-sm font-semibold"
              data-testid="pricing-plans-btn"
            >
              Weekly plans (save up to 20%)
            </Link>
          </div>
        </div>
      </section>

      {/* Fine print + FAQ */}
      <section className="max-w-4xl mx-auto px-6 md:px-10 py-20">
        <div className="overline mb-3">Small print, honestly told</div>
        <h2 className="font-display text-3xl md:text-5xl font-light tracking-tight leading-[1.05]">
          What's included, what isn't.
        </h2>
        <div className="mt-10 grid md:grid-cols-2 gap-8 text-rw-ink2 leading-relaxed">
          <div>
            <h3 className="font-display text-xl text-rw-ink mb-2">Included in every order</h3>
            <ul className="list-disc pl-5 space-y-1.5 marker:text-rw-blue">
              <li>Free doorstep pickup &amp; delivery on orders above ₹299</li>
              <li>Colour + fabric sorting before wash</li>
              <li>Temperature-controlled machines</li>
              <li>Hand ironing &amp; quality check before delivery</li>
              <li>WhatsApp updates at every status</li>
              <li>Same price across all of Gurugram — no zone surcharges</li>
            </ul>
          </div>
          <div>
            <h3 className="font-display text-xl text-rw-ink mb-2">What may cost extra</h3>
            <ul className="list-disc pl-5 space-y-1.5 marker:text-rw-blue">
              <li>Below-₹299 orders: flat ₹49 runner fee</li>
              <li>Premium Care line for embroidery / designer / silk garments</li>
              <li>Same-day rush (booked before 10 AM Mon-Sat)</li>
              <li>Stain treatment for pre-set / oil-based stains — quoted on inspection</li>
            </ul>
          </div>
        </div>

        <div className="mt-14">
          <h3 className="font-display text-2xl md:text-3xl text-rw-ink mb-6">Pricing FAQs</h3>
          <div className="space-y-6">
            <div>
              <div className="font-display text-lg text-rw-ink">
                Do you charge differently for different sectors in Gurugram?
              </div>
              <p className="mt-1 text-rw-ink2">
                No. The same rate applies all over Gurugram — DLF, Sushant Lok, Golf Course Road,
                Sohna Road, MG Road and every sector we cover.
              </p>
            </div>
            <div>
              <div className="font-display text-lg text-rw-ink">
                Is there a minimum order?
              </div>
              <p className="mt-1 text-rw-ink2">
                No minimum. Under ₹299 we add a flat ₹49 pickup fee to cover runner cost — over ₹299
                pickup &amp; delivery are free.
              </p>
            </div>
            <div>
              <div className="font-display text-lg text-rw-ink">
                Are prices locked when I book?
              </div>
              <p className="mt-1 text-rw-ink2">
                Yes. The rate shown at checkout is the rate you're billed — even if we update
                pricing between pickup and delivery.
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
